import { useState } from "react";
import type { components } from "../api/schema";

export type Impersonation = components["schemas"]["ImpersonationOut"];

/** What an administrator can't do while logged in as someone else (UX-152), for the banner's
    note. The API refuses these too. */
export function impersonationLimits(who: Impersonation): string {
  const security =
    "Password, sign-in, MCP token and Google settings can't be changed while you're logged in as them.";
  if (who.surface !== "portal") return security;
  return who.binding_actions_allowed
    ? `${security} Accepting quotes and paying are allowed here for testing; they're blocked in production.`
    : `${security} You can view quotes and invoices, but you can't accept a quote or pay on the client's behalf.`;
}

function endsAt(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

/** The caution banner on every page while an administrator is logged in as another user
    (UX-152). It can't be dismissed: the only way out is Return to my account (or signing out,
    which does the same). */
export function ImpersonationBanner({
  who,
  onReturn,
}: {
  who: Impersonation;
  onReturn: () => Promise<unknown>;
}) {
  const [busy, setBusy] = useState(false);
  const limits = impersonationLimits(who);
  return (
    <div className="imp-banner" role="region" aria-label="Logged in as another user">
      <span className="imp-icon" aria-hidden="true">
        ⚠
      </span>
      <div className="imp-text" title={limits}>
        <strong>
          You&rsquo;re logged in as {who.user_name} ({who.user_email}).
        </strong>{" "}
        <span>
          You are {who.impersonator_name} ({who.impersonator_email}). Everything you do is recorded as you
          acting as them. Ends at {endsAt(who.expires_at)}.
        </span>
        <span className="imp-limits">{limits}</span>
      </div>
      <button
        type="button"
        className="btn imp-return"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          try {
            await onReturn();
          } finally {
            setBusy(false);
          }
        }}
      >
        {busy ? "Returning…" : "Return to my account"}
      </button>
    </div>
  );
}
